import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';

/**
 * The chart's problems as a worklist.
 *
 * Every count here was previously invisible in the product — the accountant
 * restructured 428 accounts without being told that 4 groups still carried
 * placeholder names or that a child's type contradicted its parent. Each tile
 * filters the tree to exactly the rows behind it, so a number is always one
 * click from the work.
 */

export type HealthIssueKey =
  | 'autoNamedGroups'
  | 'duplicateNames'
  | 'typeMismatch'
  | 'whitespaceCodes'
  | 'orphanedByCode'
  | 'deletableLeaves'
  | 'unusedSystemAccounts'
  | 'inactiveAccounts';

interface ChartHealthPanelProps {
  /** Issue currently filtering the tree, if any. */
  activeKey: HealthIssueKey | null;
  onSelect: (key: HealthIssueKey | null, accountIds: string[]) => void;
}

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'text-red-700 dark:text-red-400',
  warning: 'text-amber-700 dark:text-amber-400',
  info: 'text-foreground',
};

export function ChartHealthPanel({ activeKey, onSelect }: ChartHealthPanelProps) {
  const { t } = useTranslation('accounting');
  const { data, isLoading } = trpc.accountWorkbench.health.useQuery();

  if (isLoading || !data) return null;

  // A clean chart should not occupy a quarter of the screen with zeroes.
  const visible = data.issues.filter((issue) => issue.count > 0);
  if (visible.length === 0) {
    return (
      <div className="border-border bg-card flex items-center gap-2 border px-4 py-2.5 text-sm">
        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
        <span className="text-muted-foreground">
          {t('chartHealth.allClear', { count: data.totalAccounts })}
        </span>
      </div>
    );
  }

  return (
    <div className="border-border bg-card border">
      <div className="border-border flex items-center justify-between border-b px-4 py-2">
        <h3 className="text-sm font-medium">{t('chartHealth.title')}</h3>
        {activeKey && (
          <button
            onClick={() => onSelect(null, [])}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            {t('chartHealth.clearFilter')}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4">
        {visible.map((issue) => {
          const isActive = activeKey === issue.key;
          return (
            <button
              key={issue.key}
              onClick={() =>
                isActive ? onSelect(null, []) : onSelect(issue.key as HealthIssueKey, issue.accountIds)
              }
              className={`border-border hover:bg-muted/50 border-b border-r p-3 text-left transition-colors ${
                isActive ? 'bg-muted' : ''
              }`}
            >
              <span
                className={`block text-xl font-semibold tabular-nums ${SEVERITY_STYLES[issue.severity]}`}
              >
                {issue.count}
              </span>
              <span className="text-muted-foreground mt-0.5 block text-xs leading-snug">
                {t(`chartHealth.issues.${issue.key}`)}
              </span>
              {issue.sampleCodes.length > 0 && (
                <span className="text-muted-foreground/70 mt-1 block truncate font-mono text-[10px]">
                  {issue.sampleCodes.join(', ')}
                  {issue.count > issue.sampleCodes.length ? ' …' : ''}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
